import { Injectable, Signal, inject } from '@angular/core';
import { getRouterSelectors } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    MobiUiLanguage,
    MobiUiLanguageService,
    MobiUiMessage,
    MobiUiMessageLevel,
    MobiUiResponsiveLayoutService,
} from '@mobi/rwc-ui-components-ng-jslib';
import {
    FeatureToggleService,
    Language,
    LanguageService,
    NgxTranslationData,
    TranslationService,
} from '@mobi/rwc-utils-ng-jslib';

import { AppSource } from '../common.constant';
import { Links } from '../core/setting';
import { APP_SETTING_PROVIDER, getLinks } from '../core/setting/app-setting.provider';
import { oivAlertActions, oivCoreSharedActions, oivOverlayActions } from '../store/core-shared/oiv-core-shared.actions';
import { oivCoreSharedFeature } from '../store/core-shared/oiv-core-shared.reducer';
import { fromOivCoreShared } from '../store/core-shared/oiv-core-shared.selector';

@Injectable({ providedIn: 'root' })
export class OivCoreSharedFacade {
    #store = inject(Store) as Store<any>;

    #appSettingProvider = inject(APP_SETTING_PROVIDER);
    #translationService = inject(TranslationService);
    #featureToggleService = inject(FeatureToggleService);
    #mobiUiLanguageService = inject(MobiUiLanguageService);
    #languageService = inject(LanguageService);
    #responsiveLayoutService = inject(MobiUiResponsiveLayoutService);

    #settings = this.#appSettingProvider.getAppSetting();

    constructor() {
        const { source, context, language, links } = this.#settings;
        this.#store.dispatch(oivCoreSharedActions.source({ source }));
        this.#store.dispatch(oivCoreSharedActions.context({ context }));
        this.#store.dispatch(oivCoreSharedActions.language({ language }));
        this.#store.dispatch(oivCoreSharedActions.links({ links }));
    }

    get appContext() {
        return this.#store.selectSignal(oivCoreSharedFeature.selectContext);
    }

    set approvalMode(isApprovalMode: boolean) {
        this.#store.dispatch(oivCoreSharedActions.approvalMode({ isApprovalMode }));
    }

    set translations(translationData: Partial<Record<Language, NgxTranslationData>>) {
        this.#translationService.setTranslations(translationData);
    }

    set language(language: Language | MobiUiLanguage) {
        this.#store.dispatch(oivCoreSharedActions.language({ language }));
    }

    updateLinks(language: MobiUiLanguage) {
        const links = getLinks(language, this.#settings.source);
        this.#store.dispatch(oivCoreSharedActions.links({ links }));
    }

    get links(): Signal<Links> {
        return this.#store.selectSignal(oivCoreSharedFeature.selectLinks);
    }

    get currentUrl() {
        return this.#store.selectSignal(getRouterSelectors().selectUrl);
    }

    get currentLanguage() {
        return this.#store.selectSignal(oivCoreSharedFeature.selectLanguage);
    }

    get isMobile() {
        return this.#store.selectSignal(oivCoreSharedFeature.selectIsMobileMode);
    }

    get isApprovalMode() {
        return this.#store.selectSignal(oivCoreSharedFeature.selectIsApprovalMode);
    }

    get notifications() {
        return this.#store.selectSignal(oivCoreSharedFeature.selectAlerts);
    }

    get appSource(): Signal<AppSource> {
        return this.#store.selectSignal(oivCoreSharedFeature.selectSource);
    }

    set appSource(source: AppSource) {
        this.#store.dispatch(oivCoreSharedActions.source({ source }));
    }

    setScreenMode() {
        this.#store.dispatch(oivCoreSharedActions.mobileMode({ isMobileMode: this.#responsiveLayoutService.mobile() }));
    }

    updateLibLanguage(language: MobiUiLanguage) {
        this.#mobiUiLanguageService.setLanguage(language);
        this.#languageService.use(language);
    }

    translatedMessage(key: string) {
        return this.#translationService.translate(key);
    }

    isFeatureToggleOn(epicId: string): Observable<boolean> {
        return this.#featureToggleService.getTreatment$(epicId).pipe(map(treatment => treatment === 'on'));
    }

    get isOverlayOpen(): Signal<boolean> {
        return this.#store.selectSignal(fromOivCoreShared.selectIsOverlayOpen);
    }

    openOverlay(): void {
        this.#store.dispatch(oivOverlayActions.open());
    }

    closeOverlay(): void {
        this.#store.dispatch(oivOverlayActions.close());
    }

    addAlert(translatedMessage: string, level: MobiUiMessageLevel) {
        this.#store.dispatch(
            oivAlertActions.add({
                id: crypto.randomUUID(),
                level: level,
                text: translatedMessage,
            }),
        );
    }

    removeAlert(messageForRemoval: MobiUiMessage) {
        this.#store.dispatch(oivAlertActions.remove(messageForRemoval));
    }

    changeLanguage(language: MobiUiLanguage | Language) {
        this.language = language;
        this.#store.dispatch(oivCoreSharedActions.changeLanguage({ language }));
    }
}
